import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Modal from '../Modal.jsx';
import Button from '../Button.jsx';
import MaterialIcon from '../../elements/MaterialIcon.jsx';
import useLoggedInUser from '../../hooks/useLoggedInUser.js';
import allReleases from './releases.js';
import { groupChanges, latestVersion, laterVersion, readLocalSeen, sortedReleases, unseenReleases } from './whatsNew.js';
import { markWhatsNewSeen } from '../../../redux/actions/whatsNew.js';

/**
 * The header's gift button, and the "What's new" notes it opens (releases.js). The notes not yet read
 * open by themselves once the app has loaded; closing them marks every note read. The button, with a
 * dot while some are unread, shows them again, and from there every older note.
 */
export default function WhatsNew({ releases = allReleases }) {
	const dispatch = useDispatch();
	const player = useLoggedInUser();
	const firstLoad = useSelector((state) => state.dashboard.firstLoad);
	const userId = player ? player.id : null;
	const seen = laterVersion(player ? player.whats_new_seen : null, readLocalSeen(userId));
	const unseen = unseenReleases(releases, seen);
	// What is open: the unread notes, every note, or nothing.
	const [showing, setShowing] = useState(null);
	// The notes that were unread when it opened, so they stay marked New while it is open.
	const [openedUnseen, setOpenedUnseen] = useState([]);
	const [origin, setOrigin] = useState(null);
	const autoOpened = useRef(false);

	const open = useCallback((which, from = null) => {
		setOpenedUnseen(unseen.map((release) => release.version));
		setOrigin(from);
		setShowing(which);
	}, [unseen]);

	useEffect(() => {
		if(!autoOpened.current && userId && !firstLoad && unseen.length > 0) {
			autoOpened.current = true;
			open('new');
		}
	}, [userId, firstLoad, unseen.length]);

	const onClose = useCallback(() => {
		setShowing(null);
		dispatch(markWhatsNewSeen(latestVersion(releases)));
	}, [dispatch, releases]);

	if(!userId || releases.length === 0) {
		return null;
	}

	const listed = showing === 'all' || openedUnseen.length === 0 ? sortedReleases(releases)
		: sortedReleases(releases).filter((release) => openedUnseen.includes(release.version));
	const hasOlder = showing === 'new' && listed.length < releases.length;

	return (
		<React.Fragment>
			<button
				type='button'
				className={`shell-control app-header__icon-link whats-new-button ${unseen.length ? 'has-unseen' : ''}`}
				aria-label={unseen.length ? 'What\'s new (unread)' : 'What\'s new'}
				title="What's new"
				onClick={(event) => open(unseen.length ? 'new' : 'all', event.currentTarget)}
			>
				<MaterialIcon>redeem</MaterialIcon>
				{unseen.length > 0 && <span className='whats-new-button__dot' aria-hidden='true' />}
			</button>
			{showing && ReactDOM.createPortal(
				<Modal
					className='whats-new-modal'
					header={showing === 'new' ? 'Fresh off the stove' : 'Everything that\'s new'}
					origin={origin}
					onClose={onClose}
				>
					{(close) => (
						<div className='whats-new'>
							{listed.map((release) => (
								<WhatsNewRelease
									key={release.version}
									release={release}
									isNew={openedUnseen.includes(release.version)}
								/>
							))}
							<div className='whats-new__actions'>
								{hasOlder &&
								<Button flat onClick={() => setShowing('all')}>Older news</Button>
								}
								<Button onClick={close}>Got it</Button>
							</div>
						</div>
					)}
				</Modal>,
				document.body
			)}
		</React.Fragment>
	);
}

export function WhatsNewRelease({ release, isNew }) {
	return (
		<section className='whats-new__release' data-version={release.version}>
			<div className='whats-new__release-heading'>
				<h5 className='whats-new__title'>{release.title}</h5>
				<span className='whats-new__date'>{moment(release.version.slice(0, 10), 'YYYY-MM-DD').format('MMMM D, YYYY')}</span>
				{isNew && <span className='whats-new__badge'>New</span>}
			</div>
			{release.intro && <p className='whats-new__intro'>{release.intro}</p>}
			{groupChanges(release).map((area) => (
				<div key={area.area} className={`whats-new__area whats-new__area--${area.area}`}>
					<h6 className='whats-new__area-label'>
						<MaterialIcon>{area.icon}</MaterialIcon>
						{area.label}
					</h6>
					{area.kinds.map((kind) => (
						<div key={kind.kind} className='whats-new__kind' data-kind={kind.kind}>
							<div className='whats-new__kind-label'>{kind.label}</div>
							<ul>
								{kind.changes.map((change) => <li key={change.text}>{change.text}</li>)}
							</ul>
						</div>
					))}
				</div>
			))}
		</section>
	);
}
